import { useRef, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { Unternehmen } from "@kundeninfo/api-client";
import { Modal, ModalFuss, useBestaetigung, darfAbbrechen } from "@kundeninfo/ui";
import { Chip, Hinweis, Knopf, Pillenreihe } from "@kundeninfo/ui/me-design";
import { hausfarbeChip } from "../markenfarben.js";
import "./UnternehmensZuordnung.css";

/**
 * Die ZUORDNUNG von Notizen und Dateien zu Unternehmen (Spec Mandantentrennung, R12, R13): Kontext,
 * kein Filter — Notizen und Dateien bleiben gemeinsam. EIN Satz Bausteine für beide Reiter.
 */
export type ZuordnungsUmfeld = {
  /** Alle sichtbaren Unternehmen — für Kürzel und Hausfarbe der Chips. */
  alle: readonly Unternehmen[];
  /** Die Unternehmen der Kundenbeziehungen — die Pillen. */
  auswahl: readonly Unternehmen[];
  /** Vorbelegung beim Anlegen einer Notiz. */
  aktivId: string | null;
};

/** Je zugeordnetem Unternehmen ein Chip; ein Unternehmen ausserhalb der Sitzung (stillgelegt) als „?".
 *  Für Hilfstechnik benannt: „Kürzel · Name", das unbekannte „Unternehmen nicht sichtbar". */
export function UnternehmensChipsAnzeige(
  p: { alle: readonly Unternehmen[]; companyIds: readonly string[]; className: string },
): ReactElement | null {
  if (p.companyIds.length === 0) return null;
  return (
    <span className={p.className} role="group" aria-label="Zugeordnete Unternehmen">
      {p.companyIds.map((id) => {
        const firma = p.alle.find((c) => c.id === id);
        return firma === undefined
          ? <Chip key={id} aria-label="Unternehmen nicht sichtbar">?</Chip>
          : <Chip key={id} {...hausfarbeChip(p.alle, firma.shortCode)}
              aria-label={`${firma.shortCode} · ${firma.name}`}>{firma.shortCode}</Chip>;
      })}
    </span>
  );
}

/** Die wählbaren Unternehmen: die der Kundenbeziehungen, dazu jedes schon zugeordnete (sonst ginge es
 *  beim Speichern stumm verloren). Reihenfolge der Sitzung. */
function waehlbar(umfeld: ZuordnungsUmfeld, wert: readonly string[]): Unternehmen[] {
  return umfeld.alle.filter((c) => umfeld.auswahl.some((a) => a.id === c.id) || wert.includes(c.id));
}

/** Eine Umschalt-Pille je wählbarem Unternehmen (`me-knopf gestalt="pille"`, Entscheid 61d); `onWert` bekommt die
 *  Menge in der Reihenfolge der Sitzung. */
export function UnternehmensSchalter(p: {
  umfeld: ZuordnungsUmfeld; wert: readonly string[]; onWert: (ids: string[]) => void;
}): ReactElement {
  const liste = waehlbar(p.umfeld, p.wert);
  return (
    <fieldset className="uzo">
      <legend className="uzo__titel">Unternehmen</legend>
      <Pillenreihe className="uzo__pillen">
        {liste.map((c) => (
          <Knopf key={c.id} art="sekundaer" gestalt="pille" gedrueckt={p.wert.includes(c.id)}
            onClick={() => p.onWert(liste.filter((x) =>
              (x.id === c.id ? !p.wert.includes(c.id) : p.wert.includes(x.id))).map((x) => x.id))}>
            {`${c.shortCode} · ${c.name}`}
          </Knopf>
        ))}
      </Pillenreihe>
    </fieldset>
  );
}

/** Gleiche Menge, gleich in welcher Reihenfolge (der Server liefert seine, die Pillen die der Sitzung). */
export function gleicheMenge(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((id) => b.includes(id));
}

/**
 * Das Panel „Unternehmen zuordnen" (Hausregeln: rechts angeschlagen mit Kreuz, Größe nach Inhalt,
 * Bestätigung vor der Datenänderung, Rückfrage beim Abbrechen mit Änderungen). Gespeichert wird die
 * GANZE Menge der sichtbaren Unternehmen (R12); Zuordnungen zu anderen erhält der Server. Ein Fehler des Schreibwegs steht IM Panel — dort, wo gearbeitet wird; die Wahl
 * bleibt stehen.
 */
export function ZuordnungsPanel(p: {
  titel: string; was: "Notiz" | "Datei"; umfeld: ZuordnungsUmfeld; ausgang: readonly string[];
  onSpeichern: (ids: string[]) => Promise<void>; onAbbrechen: () => void;
}): ReactElement {
  const [wert, setWert] = useState<string[]>([...p.ausgang]);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  // Sperre gegen Doppelklick: Der Zustand `laeuft` greift erst nach der Bestätigung und dem nächsten Zeichnen.
  const sperre = useRef(false);
  const { bestaetigen } = useBestaetigung();
  const geaendert = !gleicheMenge(wert, p.ausgang);
  const kuerzel = wert.map((id) => p.umfeld.alle.find((c) => c.id === id)?.shortCode ?? "?");
  async function speichern(): Promise<void> {
    if (sperre.current) return;
    sperre.current = true;
    try {
      if (!(await bestaetigen({
        titel: "Zuordnung ändern?",
        text: kuerzel.length === 0 ? `Die ${p.was} gehört dann zu keinem Unternehmen.`
          : `Die ${p.was} gehört dann zu: ${kuerzel.join(", ")}.`,
        bestaetigenText: "Speichern",
      }))) return;
      setLaeuft(true);
      setFehler(null);
      // Nur die sichtbaren: Zuordnungen zu anderen erhält der Server (PUT ersetzt nur innerhalb der sichtbaren).
      await p.onSpeichern(wert.filter((id) => p.umfeld.alle.some((c) => c.id === id)));
    } catch (e) {
      setFehler(e instanceof ApiError && e.message.trim() !== ""
        ? e.message : "Die Zuordnung konnte nicht gespeichert werden.");
    } finally {
      setLaeuft(false);
      sperre.current = false;
    }
  }
  async function abbrechen(): Promise<void> {
    if (laeuft) return;
    if (await darfAbbrechen(geaendert)) p.onAbbrechen();
  }
  return (
    <Modal titel={p.titel} onAbbrechen={() => { void abbrechen(); }} abbrechenGesperrt={laeuft}>
      <UnternehmensSchalter umfeld={p.umfeld} wert={wert} onWert={setWert} />
      {fehler !== null && <Hinweis art="fehler">{fehler}</Hinweis>}
      <ModalFuss speichernText="Speichern" speichernGesperrt={laeuft || !geaendert} abbrechenGesperrt={laeuft}
        onSpeichern={() => { void speichern(); }} onAbbrechen={() => { void abbrechen(); }} />
    </Modal>
  );
}
